import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

/** Pane the chat should land on — every pane with an address (SPEC-2026-09-internal-chat-pwa, T-25). */
export type ChatShellView = 'saved' | 'team' | 'search' | 'threads';

/**
 * One "open the chat here" instruction. Everything is optional — an empty
 * target just opens the bubble popup.
 */
export interface ChatShellTarget {
  /** Conversation to open (store `openConversation` is dispatched by callers or the host). */
  channelId?: string;
  /** Message to reveal inside `channelId` — the search/saved jump. */
  messageId?: string;
  /** Thread to open inside `channelId` (its root message id). */
  threadRootId?: string;
  /** Person to open a DM draft with (RF-01) — callers dispatch the draft. */
  directUserId?: string;
  /** Workspace pane — implies the expanded (fullscreen) mode. */
  view?: ChatShellView;
  /** Pre-typed term handed to the search view. */
  searchTerm?: string;
}

/**
 * Internal Chat shell bus (replacement of the retired `/chat` route).
 *
 * Every surface that used to `router.navigate(['/chat'], …)` — push
 * notification clicks, the socket-bridge toast, the quick switcher, the team
 * shortcut of the status dialog, the topbar entry — now emits a target here.
 * The always-mounted `chat-bubble-host` is the single consumer: it opens the
 * popup, expands to the fullscreen workspace when the target needs one, and
 * routes conversation/message focus to whichever mode is active.
 *
 * A plain Subject (no replay) on purpose: a target is a CLICK, not state.
 * There is no scenario where the host is not yet subscribed — it mounts with
 * the authenticated shell before any emitter can fire.
 */
@Injectable({ providedIn: 'root' })
export class ChatShellService {
  private readonly targets = new Subject<ChatShellTarget>();

  readonly targets$: Observable<ChatShellTarget> = this.targets.asObservable();

  open(target: ChatShellTarget = {}): void {
    this.targets.next(target);
  }
}
